import type { Me, Workspace } from "@omnabase/shared";
import { createFetchCache, staleAfterWrite } from "./fetchCache";
import { noticeUnauthorized, reportsSignedOut } from "./signedOut";

/** The API's own routes. Errors carry the one shape; the caller reads code and message. */
export class ApiError extends Error { constructor(readonly status: number, readonly code: string, message: string, readonly details?: unknown) { super(message); } }

/** `keepalive`: the request outlives the page (an Undo clicked just before moving on). Small bodies only. */
async function request<T>(method: string, url: string, body?: unknown, opts: { keepalive?: boolean } = {}): Promise<T> {
  if (method === "GET") return send<T>(method, url, body, opts);
  // a write drops what it may change, when it starts (a read that overlaps it is not kept) and when it is done (polish round two)
  const stale = staleAfterWrite(url); readCache.drop(stale);
  try { return await send<T>(method, url, body, opts); } finally { readCache.drop(stale); }
}
async function send<T>(method: string, url: string, body?: unknown, opts: { keepalive?: boolean } = {}): Promise<T> {
  const r = await fetch(url, { method, credentials: "same-origin", keepalive: opts.keepalive, headers: body === undefined ? {} : { "content-type": "application/json" }, body: body === undefined ? undefined : JSON.stringify(body) });
  if (r.status === 204) return undefined as T;
  if (r.status === 401 && reportsSignedOut(url)) noticeUnauthorized(); // the forum sync round: a lost session shows the signed-out sheet (lib/signedOut.ts)
  const j = await r.json().catch(() => ({}));
  if (!r.ok) { const e = (j as { error?: { code?: string; message?: string; details?: unknown } }).error; throw new ApiError(r.status, e?.code ?? "internal", e?.message ?? "Something went wrong", e?.details); }
  return j as T;
}

/** a write that does not go through `api` (an upload, the auth library) tells the shared fetch layer what it may have changed */
export const wrote = (url: string) => readCache.drop(staleAfterWrite(url));

/** The shared fetch layer (polish round two): one memory per tab for the app's reads, keyed by address (the workspace is in it) and session.
 *  Screens read through `api.cached` (reuse) and reload through `api.fresh`; `api.get` stays a plain request for the few reads that must
 *  never be reused (a poll, a check right after a write elsewhere). */
export const readCache = createFetchCache();
export const api = {
  get: <T>(url: string) => request<T>("GET", url),
  /** the request in flight or an answer from the last few seconds, else a new request */
  cached: <T>(url: string) => readCache.read<T>(url, () => request<T>("GET", url)),
  /** a new request (joining one for the same address started a moment ago: one live note, two hooks) */
  fresh: <T>(url: string) => readCache.refresh<T>(url, () => request<T>("GET", url)),
  post: <T>(url: string, body?: unknown) => request<T>("POST", url, body),
  put: <T>(url: string, body?: unknown) => request<T>("PUT", url, body),
  patch: <T>(url: string, body?: unknown, opts?: { keepalive?: boolean }) => request<T>("PATCH", url, body, opts),
  del: <T>(url: string, body?: unknown, opts?: { keepalive?: boolean }) => request<T>("DELETE", url, body, opts),
};

/** who is signed in; `cached` for the app's first read (React's double mount asks once), `fresh` after signing in or joining */
export async function fetchMe(how: "cached" | "fresh" = "fresh"): Promise<Me | null> {
  try { return await (how === "cached" ? api.cached<Me>("/api/v1/me") : api.get<Me>("/api/v1/me")); } catch (e) { if (e instanceof ApiError && e.status === 401) return null; throw e; }
}
export const workspaceUrl = (slug: string) => `/api/v1/w/${encodeURIComponent(slug)}`;
export const fetchWorkspace = (slug: string) => api.get<Workspace>(workspaceUrl(slug));
